import React, { useCallback, useEffect, useState } from 'react';

import Checkbox from '../CheckBox';
import Fieldset from '../shared/Fieldset';

import s from './Checkboxes.module.scss';

export type CheckboxOption = {
  key: string;
  label: string;
};

export type CheckboxesProps = {
  options: CheckboxOption[];
  label: string;
  description?: string;
  disabled?: boolean;
  hint?: string;
  value: string[] | null;
  optional?: boolean;
  onChange: (value: string[]) => void;
  onBlur?: () => void;
  /** Called when the last selected option is unchecked, to trigger required-field validation without the side effects of a real blur event. */
  onRequiredEmpty?: () => void;
  onFocus?: () => void;
  compact?: boolean;
  error?: string;
  hasError?: boolean;
};

function isValueChecked(checkedValues: unknown, key: string): boolean {
  if (Array.isArray(checkedValues)) {
    return checkedValues.includes(key);
  }
  return [checkedValues].includes(key);
}

const Checkboxes: React.FC<CheckboxesProps> = ({
  options,
  optional,
  label,
  description,
  hint,
  value,
  onChange,
  onBlur,
  onRequiredEmpty,
  onFocus,
  disabled,
  compact,
  error,
  hasError,
}) => {
  const [checkedValues, setCheckedValues] = useState<string[]>(value ?? []);

  useEffect(() => {
    setCheckedValues(value ?? []);
  }, [value]);

  const handleChange = useCallback(
    (v: string, newIsChecked: boolean): void => {
      const newValue = newIsChecked ? [...checkedValues, v] : checkedValues.filter((val) => val !== v);
      setCheckedValues(newValue);
      onChange(newValue);
      // When unchecking removes the last selection, notify the parent so it can trigger
      // required-field validation. Using a dedicated callback avoids the side effects of
      // a real blur event (e.g. fieldEditingStateChanged) while focus is still in the group.
      if (newValue.length === 0) {
        onRequiredEmpty?.();
      }
    },
    [checkedValues, onChange, onRequiredEmpty],
  );

  const handleBlur = useCallback(
    (event: React.FocusEvent<HTMLDivElement>): void => {
      // Fire onBlur only when focus leaves the entire group, not when moving between checkboxes.
      if (!event.currentTarget.contains(event.relatedTarget instanceof Node ? event.relatedTarget : null)) {
        onBlur?.();
      }
    },
    [onBlur],
  );

  return (
    <Fieldset error={error} label={label} description={description} hint={hint} required={!optional}>
      <div onBlur={handleBlur}>
        {options.map((option) => (
          <div key={option.key} className={s.item}>
            <Checkbox
              className={s.checkbox}
              disabled={disabled}
              id={option.key}
              isChecked={isValueChecked(checkedValues, option.key)}
              hasError={hasError || !!error}
              label={option.label}
              onChange={(newIsChecked) => handleChange(option.key, newIsChecked)}
              onFocus={onFocus}
              size={compact ? 'sm' : 'md'}
            />
          </div>
        ))}
      </div>
    </Fieldset>
  );
};

export default Checkboxes;
